import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import CollapsePanel from 'app/componentV4/Collapses/CollapsePanel'
import useAccountInfo, { collapseKey } from './useAccountInfo'
import AccountDetail from './AccountDetail'
import NotesSection from './NotesSection'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import useNotes from './useNotes'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY } from 'app/constants/button'
import { makeStyles, TextField, Typography } from '@material-ui/core'
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined'
import clsx from 'clsx'
import SimpleDialog from 'app/componentV4/Dialog/SimpleDialog'
import { isNil } from 'ramda'
import { Field, Form, Formik } from 'formik'

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '350px',
    },
  },
  button: {
    '& .MuiButtonBase-root': {
      borderRadius: '4px !important',
      border: '1px solid var(--Primary-300, #5D7290) !important',
      lineHeight: 'normal !important',
      padding: '2px 4px !important',
      fontSize: '14px !important',
    },
  },
  buttonClose: {
    '& .MuiButtonBase-root': {
      borderRadius: '4px !important',
      border: '1px solid var(--Primary-300, #5D7290) !important',
    },
    '& .Mui-disabled': {
      border: 'unset !important',
    },
  },
  noFocusBorder: {
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ccc', // Keep the same border on hover
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ccc !important', // Force border to stay the same on focus
      },
    },
  },
})

const AccountInfoTab = ({ subscriber, languages }) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const {
    state: { account, note },
    toggleCollapse,
  } = useAccountInfo()

  const {
    state: { notes, isLoading, noteDetail, openView, openFormDialog },
    handleFetchNotes,
    onUpdateNotes,
    onDeleteNote,
    onCreateNote,
    onOpenViewNote,
    closeViewNote,
    onOpenFormDialog,
    onCloseFormDialog,
  } = useNotes()

  useEffect(() => {
    handleFetchNotes({ subscriberId: subscriber.id })
  }, [])

  return (
    <div className="space-y-5">
      {subscriber.account.isParentAvailable && (
        <CollapsePanel
          open={account}
          keys={collapseKey.account}
          title={formatMessage({ id: 'registerForm.accountDetails.title' })}
          toggleCollapse={() => toggleCollapse(collapseKey.account)}
          customHeadTab={
            <div>
              <PrimaryTooltip title={formatMessage({ id: 'crm.help.text' })}>
                <InfoOutlinedIcon fontSize="small" />
              </PrimaryTooltip>
            </div>
          }
        >
          <AccountDetail
            accountData={subscriber.account}
            languages={languages}
          />
        </CollapsePanel>
      )}
      <CollapsePanel
        open={note}
        keys={collapseKey.note}
        title={formatMessage({ id: 'subscriber.label.notes' })}
        toggleCollapse={() => toggleCollapse(collapseKey.note)}
        customHeadTab={
          notes.length > 0 ? (
            <div className={clsx(classes.button, 'cursor-pointer')}>
              <ContainedButton
                color={CLOSE}
                onClick={(event) => {
                  event.stopPropagation()
                  onOpenFormDialog()
                }}
                type="button"
              >
                <div className="flex space-x-4 font-normal text-14 justify-center items-center">
                  <AddBoxOutlinedIcon fontSize="small" />
                  <span className="capitalize">
                    {formatMessage({
                      id: 'note.create.addNote.label',
                    })}
                  </span>
                </div>
              </ContainedButton>
            </div>
          ) : null
        }
      >
        <NotesSection
          subscriber={subscriber}
          notes={notes}
          isLoading={isLoading}
          noteDetail={noteDetail}
          openView={openView}
          onOpenFormDialog={onOpenFormDialog}
          onDeleteNote={onDeleteNote}
          onOpenViewNote={onOpenViewNote}
          closeViewNote={closeViewNote}
        />
      </CollapsePanel>
      <SimpleDialog
        id="form-note"
        open={openFormDialog}
        title={
          <div className="my-auto">
            <Typography className="text-14 font-bold text-blue-950 px-5">
              {formatMessage({
                id: isNil(noteDetail)
                  ? 'note.create.addNote.label'
                  : 'note.details.title',
              })}
            </Typography>
          </div>
        }
        onClose={closeViewNote}
        className={classes.root}
      >
        <div className="p-5 space-y-5 w-full sm:w-512">
          <Formik
            initialValues={{ text: noteDetail?.text || '' }}
            onSubmit={(values) => {
              if (isNil(noteDetail)) {
                onCreateNote({ text: values.text, subscriberId: subscriber.id })
              } else {
                onUpdateNotes({
                  text: values.text,
                  subscriberId: subscriber.id,
                  noteId: noteDetail.id,
                })
              }
            }}
          >
            {({ handleSubmit, dirty, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <Field
                  name="text"
                  as={TextField}
                  placeholder={formatMessage({
                    id: 'subscriberDetail.notes.add.placeholder',
                  })}
                  variant="outlined"
                  multiline
                  rows={4}
                  fullWidth
                  className={classes.noFocusBorder}
                />
                <div className="flex justify-center space-x-5 mt-5">
                  <div className={clsx(classes.buttonClose, 'cursor-pointer')}>
                    <ContainedButton
                      color={CLOSE}
                      onClick={onCloseFormDialog}
                      type="button"
                      className="px-5 py-4"
                      disabled={isSubmitting}
                    >
                      <div className="flex">
                        <span className="normal-case">
                          {formatMessage({
                            id: 'text.close',
                          })}
                        </span>
                      </div>
                    </ContainedButton>
                  </div>

                  <ContainedButton
                    color={SECONDARY}
                    disabled={!dirty || isSubmitting}
                    isLoading={isSubmitting}
                    type="submit"
                    className="px-5 py-4"
                  >
                    <div className="flex">
                      <span className="normal-case">
                        {formatMessage({
                          id: isNil(noteDetail)
                            ? 'note.create.addNote.button'
                            : 'action.update',
                        })}
                      </span>
                    </div>
                  </ContainedButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </SimpleDialog>
    </div>
  )
}

export default AccountInfoTab
